import React, { createContext, useContext, useEffect, useState } from "react";
import api, { UserProfileDto, UserProfileDtoRole } from "../api";

export type AuthUser = UserProfileDto;
export type ThemeNames = "dark"|"light"|"dark-blue"|"light-blue";

type UserContextType = {
  themeName: ThemeNames;
  changeThemeName: (themeName: ThemeNames) => void;
  user: AuthUser;
  // setUser: React.Dispatch<React.SetStateAction<AuthUser>>
  isSignedIn: () => boolean;
  isAdmin: () => boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => void;
};

type UserContextProviderProps = {
  children: React.ReactNode;
};

const UserContext = createContext<UserContextType | null>(null);

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const init = {
    themeName: (localStorage.getItem("theme") || "dark-blue") as ThemeNames,
    token: localStorage.getItem("token") || "",
    user: JSON.parse(localStorage.getItem("user") || "{}") as AuthUser,
  };
  api.token = init.token;

  const [themeName, setThemeName] = useState<ThemeNames>(init.themeName);

  const changeThemeName = (themeName: ThemeNames) : void => {
    localStorage.setItem("theme", themeName);
    setThemeName(themeName);
  }

  const [user, setUser] = useState<AuthUser>(init.user);

  const isSignedIn = (): boolean => !!user?.email;

  const isAdmin = (): boolean => user?.role === UserProfileDtoRole.Admin;

  const login = async (email: string, password: string): Promise<AuthUser> => {
    try {
      const auth = await api.auth.login({ email, password } as any);
      const profile = await api.auth.profile({ token: auth.token } as any);
      localStorage.setItem("user", JSON.stringify(profile));
      localStorage.setItem("token", auth.token);
      api.token = auth.token;
      setUser(profile);
      return profile;
    } catch (err: any) {
      const message = err?.response?.message ?? err?.message;
      throw new Error(message);
    }
  };

  const logout = (): void => {
    localStorage.clear();
    setUser({} as AuthUser);
  };

  useEffect(() => {
    const verifyAuthorizedUser = async () => {
      try {
        if (!!user.email && api.token) {
          const profile = await api.user.profile();
          if (!profile || profile.id !== user.id || profile.email !== user.email || profile.role !== user.role) {
            logout();
          }
        }
      } catch (err: any) {
        // const message = err?.response?.message ?? err?.message;
        logout();
      }
    };
    verifyAuthorizedUser();
  }, [user]);

  useEffect(() => {
    const interceptor = api.axiosInstance.interceptors.response.use((res) => {
      return res;
    }, (err) => {
      if (err?.response?.status === 401) {
        logout();
      }
      return Promise.reject(err);
    });
    return () => api.axiosInstance.interceptors.response.eject(interceptor);
  }, [])

  return <UserContext.Provider value={{ themeName, changeThemeName, user, isSignedIn, isAdmin, login, logout }}>{children}</UserContext.Provider>;
};

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error("UserContext not found");
  }
  return context;
};
